<template>
  <div
    class="viluu-landing viluu-landing--subpage"
    :class="{ 'is-leaving': isLeaving, 'from-bfcache': fromBfcache }"
  >
    <BgBlobs />
    <TheNav
      v-if="!isMobile"
      variant="subpage"
      :active-key="navActive"
    />

    <header class="page-header">
      <span
        v-if="eyebrow"
        class="page-eyebrow"
      >{{ eyebrow }}</span>
      <h1 class="page-title">
        <template v-if="titlePrefix">{{ titlePrefix }}
        </template>
        <span
          v-if="titleGrad"
          class="grad"
        >{{ titleGrad }}</span>
        <span
          v-if="titleSuffix"
          class="title-dot"
        >{{ titleSuffix }}</span>
      </h1>
      <p
        v-if="$slots.lede"
        class="page-lede"
      >
        <slot name="lede" />
      </p>
      <p
        v-else-if="ledePrefix || ledeStrong || ledeSuffix"
        class="page-lede"
      >
        <template v-if="ledePrefix">{{ ledePrefix }}</template>
        <strong
          v-if="ledeStrong"
          class="ink"
        >{{ ledeStrong }}</strong>
        <template v-if="ledeSuffix">{{ ledeSuffix }}</template>
      </p>
      <div
        v-if="meta"
        class="page-meta"
      >
        {{ meta }}
      </div>
    </header>

    <!-- Optional jugendschutz card (legal pages render it just under the header) -->
<!--    <div-->
<!--      v-if="showJugendschutz"-->
<!--      class="jugendschutz-card"-->
<!--    >-->
<!--      <a-->
<!--        href="https://www.jugendschutzbeauftragter.com"-->
<!--        target="_blank"-->
<!--        rel="noopener"-->
<!--        :aria-label="trans.get('landing.subpages_shared.jugendschutz_link_aria')"-->
<!--      >-->
<!--        <img-->
<!--          src="../../../../../../images/viluu/landing-v2/badges/jugendschutzbeauftragter.png"-->
<!--          :alt="trans.get('landing.subpages_shared.jugendschutz_badge_alt')"-->
<!--          width="80"-->
<!--          height="80"-->
<!--          loading="lazy"-->
<!--        />-->
<!--      </a>-->
<!--      <div class="jugendschutz-info">-->
<!--        <strong>{{ trans.get('landing.subpages_shared.jugendschutz_label') }}</strong>-->
<!--        <span>{{ trans.get('landing.subpages_shared.jugendschutz_contact_name') }}</span>-->
<!--        <span>-->
<!--          {{ trans.get('landing.subpages_shared.jugendschutz_contact_email_label') }}-->
<!--          <a href="mailto:info@jugendschutzbeauftragter.com">info@jugendschutzbeauftragter.com</a>-->
<!--        </span>-->
<!--        <span>-->
<!--          <a-->
<!--            href="https://www.jugendschutzbeauftragter.com"-->
<!--            target="_blank"-->
<!--            rel="noopener"-->
<!--          >www.jugendschutzbeauftragter.com</a>-->
<!--        </span>-->
<!--      </div>-->
<!--    </div>-->

    <!-- Page content is provided by the wrapping subpage component -->
    <slot />

    <!-- Closing CTA band (matches the dark gradient card on every viluu-site subpage) -->
    <section
      v-if="!isMobile && showCta"
      class="cta-band"
    >
      <h2>
        <template v-if="resolvedCtaTitle.prefix">{{ resolvedCtaTitle.prefix }}
        </template>
        <span
          v-if="resolvedCtaTitle.grad"
          class="grad"
        >{{ resolvedCtaTitle.grad }}</span>
        <template v-if="resolvedCtaTitle.suffix">{{ resolvedCtaTitle.suffix }}</template>
      </h2>
      <p>{{ resolvedCtaLede }}</p>
      <button
        type="button"
        class="cta-pill"
        @click="openSignup"
      >
        {{ resolvedCtaButton }} <span aria-hidden="true">→</span>
      </button>
    </section>

    <TheFooter v-if="!isMobile" :variant="footerVariant" />
    <SignupModal v-if="!isMobile" />
    <CookieBanner v-if="!isMobile" />
  </div>
</template>

<script>
import { landingChromeMixin } from '../landingChrome';
import BgBlobs from '../../../../components/LandingV2/BgBlobs.vue';
import CookieBanner from '../../../../components/LandingV2/CookieBanner.vue';
import TheNav from '../../../../components/LandingV2/sections/TheNav.vue';
import TheFooter from '../../../../components/LandingV2/sections/TheFooter.vue';
import SignupModal from '../../../../components/LandingV2/signup/SignupModal.vue';

export default {
  name: 'SubpageShell',
  mixins: [landingChromeMixin],
  components: { BgBlobs, CookieBanner, TheNav, TheFooter, SignupModal },
  props: {
    eyebrow: { type: String, default: '' },
    titlePrefix: { type: String, default: '' },
    titleGrad: { type: String, default: '' },
    titleSuffix: { type: String, default: '' },
    ledePrefix: { type: String, default: '' },
    ledeStrong: { type: String, default: '' },
    ledeSuffix: { type: String, default: '' },
    meta: { type: String, default: '' },
    showJugendschutz: { type: Boolean, default: false },
    showCta: { type: Boolean, default: true },
    // 'subpage' (default) — Städte footer; 'legal' — agb/impressum/datenschutz footer.
    footerVariant: {
      type: String,
      default: 'subpage',
      validator: (v) => ['subpage', 'legal'].includes(v),
    },
    ctaTitlePrefix: { type: String, default: null },
    ctaTitleGrad: { type: String, default: null },
    ctaTitleSuffix: { type: String, default: null },
    ctaLede: { type: String, default: null },
    ctaButton: { type: String, default: null },
    // Key of the nav link to highlight as active; null highlights none.
    navActive: { type: String, default: null },
  },
  computed: {
    // The landing's big-cta translations are the source-of-truth defaults
    // (matches the dark CTA card the static viluu-site uses on every page).
    // Subpages may override individually via props for page-specific copy.
    resolvedCtaTitle() {
      return {
        prefix:
          this.ctaTitlePrefix != null
            ? this.ctaTitlePrefix
            : this.trans.get('landing.big_cta.title_prefix'),
        grad:
          this.ctaTitleGrad != null
            ? this.ctaTitleGrad
            : this.trans.get('landing.big_cta.title_grad'),
        suffix:
          this.ctaTitleSuffix != null
            ? this.ctaTitleSuffix
            : this.trans.get('landing.big_cta.title_suffix'),
      };
    },
    resolvedCtaLede() {
      return this.ctaLede || this.trans.get('landing.big_cta.text');
    },
    resolvedCtaButton() {
      // The translated string ends with " →"; strip it because the template
      // appends the arrow as a separate span (so it animates on hover).
      const raw = this.ctaButton || this.trans.get('landing.big_cta.cta');
      return raw.replace(/\s*→\s*$/, '').trim();
    },
    isMobile() {
      return window.mobile && typeof window.mobile.platform;
    }
  },
  methods: {
    openSignup() {
      this.$eventHub.$emit('viluu-landing:open-signup', { mode: 'register' });
    },
  },
};
</script>
<style scoped lang="scss">
.page-header {
  position: relative;
  z-index: 5;
  max-width: 880px;
  margin: 0 auto;
  padding: 60px 40px 30px;
}

.page-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 99px;
  background: rgba(198, 62, 124, 0.08);
  color: var(--pink);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 18px;
}

.page-eyebrow::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--pink);
}

.page-title {
  font-family: var(--display);
  font-size: clamp(40px, 5.6vw, 64px);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.05;
  margin: 0 0 18px;
  text-wrap: balance;
  overflow-wrap: normal;
}

.page-title .grad {
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  /* The gradient word is italic, so its slanted leading edge sits further right
     and the space after the upright prefix looks too wide. Pull it back a touch
     so the join reads like a normal word space (mirrors the right-side nudge in
     `.grad + .title-dot`). */
  margin-left: -0.08em;
}

.page-lede {
  font-size: 17px;
  line-height: 1.6;
  color: var(--ink-dim);
  margin: 0;
  text-wrap: pretty;
  max-width: 640px;
}

.page-meta {
  margin-top: 14px;
  font-size: 12.5px;
  color: var(--ink-mute);
}

.cta-band {
  position: relative;
  z-index: 5;
  max-width: 1480px;
  margin: 80px auto 0;
  padding: 60px 40px;
  background: linear-gradient(135deg, #170810 0%, #2d1420 50%, #c63e7c 130%);
  color: #fff;
  overflow: hidden;
  text-align: center;
  border-radius: 32px;
}

.cta-band::before {
  content: '';
  position: absolute;
  top: -180px;
  right: -180px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--coral), transparent 60%);
  opacity: 0.4;
  filter: blur(40px);
}

.cta-band::after {
  content: '';
  position: absolute;
  bottom: -180px;
  left: -180px;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--pink), transparent 60%);
  opacity: 0.5;
  filter: blur(40px);
}

.cta-band > * {
  position: relative;
  z-index: 1;
}

.cta-band h2 {
  font-family: var(--display);
  font-size: clamp(28px, 4.6vw, 48px);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0 0 14px;
  text-wrap: balance;
}

.cta-band p {
  font-size: 16px;
  opacity: 0.85;
  max-width: 560px;
  margin: 0 auto 28px;
  line-height: 1.5;
}

.cta-band .cta-pill {
  font: inherit;
  font-weight: 600;
  font-size: 15px;
  color: #fff;
  background: linear-gradient(135deg, var(--pink), var(--coral));
  border: none;
  padding: 16px 28px;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 14px 30px -8px rgba(255, 59, 127, 0.55);
  text-decoration: none;
  transition: transform 0.25s, box-shadow 0.25s;
}

.cta-band .cta-pill:hover {
  box-shadow: 0 20px 40px -10px rgba(255, 59, 127, 0.65);
}

.jugendschutz-card {
  display: flex;
  align-items: center;
  gap: 18px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 18px 22px;
  margin: 0 auto 32px;
  max-width: 760px;
  box-shadow: 0 8px 22px -10px rgba(20, 5, 15, 0.1);
}

.jugendschutz-card img {
  flex-shrink: 0;
  display: block;
  width: 80px;
  height: 80px;
}

.jugendschutz-card .jugendschutz-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--ink-dim);
}

.jugendschutz-card .jugendschutz-info strong {
  color: var(--ink);
  font-weight: 600;
  margin-bottom: 2px;
}

.jugendschutz-card .jugendschutz-info a {
  color: var(--pink);
  text-decoration: none;
}

.jugendschutz-card .jugendschutz-info a:hover {
  text-decoration: underline;
}

@media (max-width: 700px) {
  .page-header {
    padding: 40px 24px 20px;
  }

  .page-title {
    /* balance can keep long words (e.g. Geschäftsbedingungen) on one line */
    text-wrap: wrap;
    /* break inside a word only when it would overflow the line */
    overflow-wrap: break-word;
  }
}

@media (max-width: 700px) {
  .cta-band {
    margin: 48px 20px 0;
    padding: 44px 24px;
    border-radius: 24px;
  }
}
</style>
